import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  BookOpen,
  HeartHandshake,
  MessageCircle,
  NotebookPen,
  Sparkles,
  TrendingUp
} from 'lucide-react';
import { PERSONALIZED_ONBOARDING_STORAGE_KEY } from '../utils/onboardingRecommendations';
import { persistence } from '../services/persistence';

export type OnboardingStepPlacement = 'center' | 'right' | 'left' | 'bottom';

export interface OnboardingStep {
  id: string;
  title: string;
  description: React.ReactNode;
  icon: React.ReactNode;
  targetId?: string;
  placement?: OnboardingStepPlacement;
  highlightPadding?: number;
}

interface OnboardingContextValue {
  isOpen: boolean;
  steps: OnboardingStep[];
  currentStepIndex: number;
  currentStep: OnboardingStep;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  skipOnboarding: () => void;
  closeOnboarding: () => void; // Close without triggering onComplete
  startOnboarding: (force?: boolean) => void;
  restartOnboarding: () => void;
  registerElement: (id: string, element: HTMLElement | null) => void;
  getElementForId: (id: string) => HTMLElement | null;
  hasCompleted: boolean;
  onFetchFrom1177?: () => void;
  // Personalized onboarding
  hasCompletedPersonalized: boolean;
  startFeatureTour: () => void;
  startPersonalizedOnboarding: () => void; // Trigger personalized onboarding
}

const ONBOARDING_STORAGE_KEY = 'eir-onboarding-completed';

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to EIR Space',
    icon: <Sparkles size={26} />,
    placement: 'center',
    description: (
      <>
        <p>
          EIR Space is your companion for understanding and acting on your health data. We created a calm workflow that blends
          Swedish healthcare records with your own reflections so you always know what to do next.
        </p>
        <p>
          Let us walk you through the most important spaces so that you feel confident from your very first visit.
        </p>
      </>
    )
  },
  {
    id: 'track-your-health',
    title: 'Track Your Health',
    icon: <NotebookPen size={26} />,
    targetId: 'nav-track-health',
    placement: 'right',
    highlightPadding: 14,
    description: (
      <>
        <p>
          Track Your Health brings together your complete health picture from three sources:
        </p>
        <ul>
          <li><strong>Electronic Health Records</strong> from Swedish 1177 and other healthcare systems</li>
          <li><strong>Personal Health Journal</strong> where you record daily reflections, symptoms, nutrition, activity, sleep, and more</li>
          <li><strong>AI Journal Notes</strong> generated through AI health assessments</li>
        </ul>
        <p>
          Load your health data from the Profile menu, then explore your complete health timeline in one unified view.
        </p>
      </>
    )
  },
  {
    id: 'chat-with-ai',
    title: 'Chat with AI',
    icon: <MessageCircle size={26} />,
    targetId: 'chat-toggle',
    placement: 'left',
    highlightPadding: 20,
    description: (
      <>
        <p>
          The AI assistant turns your health records into a conversational coach. Ask plain-language questions and get sourced answers about your health.
        </p>
        <ul>
          <li>Dive into visit notes, lab values, and medication changes</li>
          <li>Get explanations of medical terms and conditions</li>
          <li>Start an AI health assessment to get personalized insights</li>
          <li>Use it as a study partner before appointments or when symptoms change</li>
        </ul>
        <p>
          Click the chat button anytime to get help understanding your health data.
        </p>
      </>
    )
  },
  {
    id: 'know-your-health',
    title: 'Know Your Health',
    icon: <Sparkles size={26} />,
    targetId: 'nav-know-health',
    placement: 'right',
    highlightPadding: 14,
    description: (
      <>
        <p>
          Understand your health through two powerful methods:
        </p>
        <ul>
          <li><strong>Self-Assessment Tests</strong> - Take structured health assessments to get insights into your wellbeing</li>
          <li><strong>AI Health Assessment</strong> - After completing 3 assessments, unlock your personalized AI health assessment</li>
        </ul>
        <p>
          The AI assessment analyzes patterns across all your health data to provide comprehensive insights and recommendations.
        </p>
      </>
    )
  },
  {
    id: 'improve-your-health',
    title: 'Improve Your Health',
    icon: <TrendingUp size={26} />,
    targetId: 'nav-grow-self',
    placement: 'right',
    highlightPadding: 14,
    description: (
      <>
        <p>
          Improve Your Health combines powerful tools for personal development and wellbeing:
        </p>
        <ul>
          <li><strong>Self-Awareness</strong> - Feedback Studio, Shadow Work, Understanding Emotions, and Understanding Relationships</li>
          <li><strong>Physical Health</strong> - Sleep Upgrade, Food & Nutrition, Water & Drinks, and Movement & Activity</li>
          <li><strong>Mental Health</strong> - Health Programs (CBT-based), Brain Training, Puzzle Studio, and Health Literacy</li>
          <li><strong>Daily Wellness</strong> - Habit Tracker, Calendar, Voice Practice, Breathing Exercises, and Writing Practice</li>
          <li><strong>Health Actions</strong> - Turn insights into concrete, actionable steps that improve your health</li>
        </ul>
        <p>
          Grow through improved understanding of yourself and actions aligned with your goals. Explore feedback from others, take actionable steps to improve your health, and engage with evidence-based cognitive behavioral therapy programs.
        </p>
      </>
    )
  },
  {
    id: 'get-care',
    title: 'Get Care',
    icon: <HeartHandshake size={26} />,
    targetId: 'nav-get-care',
    placement: 'right',
    highlightPadding: 14,
    description: (
      <>
        <p>
          Connect with the healthcare system and get the support you need:
        </p>
        <ul>
          <li><strong>Health Map</strong> - Find healthcare facilities near you across Sweden</li>
          <li><strong>Request Care</strong> - Generate professional care request documents to share with healthcare providers</li>
        </ul>
        <p>
          You are ultimately responsible for your health, but you have the help of an entire healthcare system to guide you.
        </p>
      </>
    )
  },
  {
    id: 'eir-docs',
    title: 'Eir Docs',
    icon: <BookOpen size={26} />,
    targetId: 'nav-eir-docs',
    placement: 'right',
    highlightPadding: 14,
    description: (
      <>
        <p>
          Access comprehensive documentation and resources:
        </p>
        <ul>
          <li><strong>Tutorial</strong> - Step-by-step guides to get started</li>
          <li><strong>Documentation</strong> - Detailed information about all features and their benefits</li>
          <li><strong>App Feedback</strong> - Share your experience and help us improve</li>
          <li><strong>Roadmap</strong> - See what's coming and suggest new features</li>
        </ul>
        <p>
          Everything you need to make the most of EIR Space is right here.
        </p>
      </>
    )
  }
];

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

interface OnboardingProviderProps {
  children: React.ReactNode;
  onComplete?: () => void;
  onFetchFrom1177?: () => void;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children, onComplete, onFetchFrom1177 }) => {
  const [hasCompleted, setHasCompleted] = useState<boolean>(true);
  const [hasCompletedPersonalized, setHasCompletedPersonalized] = useState<boolean>(false); // Default to false - assume not completed until checked
  const [isOpen, setIsOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [elements, setElements] = useState<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    const stored = persistence.getItem(ONBOARDING_STORAGE_KEY);
    setHasCompleted(stored === 'true');

    const personalizedStored = persistence.getItem(PERSONALIZED_ONBOARDING_STORAGE_KEY);
    setHasCompletedPersonalized(personalizedStored === 'true');
  }, []);

  const isElementVisible = useCallback((element: HTMLElement | null) => {
    if (!element) {
      return false;
    }

    if (typeof window === 'undefined') {
      return true;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return false;
    }

    const computed = window.getComputedStyle(element);
    if (computed.display === 'none' || computed.visibility === 'hidden') {
      return false;
    }

    const opacity = parseFloat(computed.opacity || '1');
    return opacity > 0.05;
  }, []);

  const registerElement = useCallback((id: string, element: HTMLElement | null) => {
    setElements(prev => {
      const current = prev[id] ?? null;

      if (!element) {
        if (!current) {
          return prev;
        }
        const next = { ...prev };
        delete next[id];
        return next;
      }

      const elementVisible = isElementVisible(element);
      const currentVisible = isElementVisible(current);

      if (!elementVisible) {
        if (!currentVisible && current) {
          const next = { ...prev };
          delete next[id];
          return next;
        }
        return prev;
      }

      if (current === element) {
        return prev;
      }

      if (!currentVisible || !current) {
        return {
          ...prev,
          [id]: element
        };
      }

      const currentRect = current.getBoundingClientRect();
      const newRect = element.getBoundingClientRect();
      const currentArea = currentRect.width * currentRect.height;
      const newArea = newRect.width * newRect.height;

      if (newArea >= currentArea) {
        return {
          ...prev,
          [id]: element
        };
      }

      return prev;
    });
  }, [isElementVisible]);

  const getElementForId = useCallback(
    (id: string) => {
      return elements[id] ?? null;
    },
    [elements]
  );

  const startOnboarding = useCallback(
    (force = false) => {
      if (!force && hasCompleted) {
        return;
      }
      setCurrentStepIndex(0);
      setIsOpen(true);
    },
    [hasCompleted]
  );

  // DISABLED: Old onboarding no longer auto-triggers
  // Users can manually trigger it from the profile if they want
  // useEffect(() => {
  //   if (typeof window === 'undefined') {
  //     return;
  //   }
  //   // IMPORTANT: Only auto-start the old feature tour if:
  //   // 1. The old onboarding hasn't been completed
  //   // 2. It hasn't been launched yet in this session
  //   // 3. The personalized onboarding HAS been completed (so new users only see personalized)
  //   // This ensures new users only see the personalized onboarding, not both.
  //   // 
  //   // Check localStorage directly to avoid race conditions with state initialization
  //   const personalizedStored = window.localStorage.getItem(PERSONALIZED_ONBOARDING_STORAGE_KEY);
  //   const personalizedCompleted = personalizedStored === 'true';
  //   
  //   if (!hasCompleted && !hasLaunched && personalizedCompleted) {
  //     const timer = window.setTimeout(() => {
  //       startOnboarding(true);
  //     }, 600);

  //     return () => window.clearTimeout(timer);
  //   }
  // }, [hasCompleted, hasLaunched, hasCompletedPersonalized, startOnboarding]);

  const completeOnboarding = useCallback(() => {
    setIsOpen(false);
    setHasCompleted(true);
    persistence.setItem(ONBOARDING_STORAGE_KEY, 'true');
    if (onComplete) {
      setTimeout(() => {
        onComplete();
      }, 100);
    }
  }, [onComplete]);

  const skipOnboarding = useCallback(() => {
    completeOnboarding();
  }, [completeOnboarding]);

  const closeOnboarding = useCallback(() => {
    setIsOpen(false);
    setHasCompleted(true);
    persistence.setItem(ONBOARDING_STORAGE_KEY, 'true');
  }, []);

  const restartOnboarding = useCallback(() => {
    persistence.removeItem(ONBOARDING_STORAGE_KEY);
    setHasCompleted(false);
    startOnboarding(true);
  }, [startOnboarding]);

  const startFeatureTour = useCallback(() => {
    persistence.removeItem(ONBOARDING_STORAGE_KEY);
    setHasCompleted(false);
    startOnboarding(true);
  }, [startOnboarding]);

  const startPersonalizedOnboarding = useCallback(() => {
    persistence.setItem('eir-trigger-personalized-onboarding', 'true');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('eir-trigger-personalized-onboarding'));
    }
  }, []);

  const goToNextStep = useCallback(() => {
    setCurrentStepIndex(prevIndex => {
      if (prevIndex >= ONBOARDING_STEPS.length - 1) {
        completeOnboarding();
        return prevIndex;
      }
      return prevIndex + 1;
    });
  }, [completeOnboarding]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStepIndex(prevIndex => Math.max(0, prevIndex - 1));
  }, []);

  const value = useMemo<OnboardingContextValue>(() => {
    const step = ONBOARDING_STEPS[currentStepIndex] ?? ONBOARDING_STEPS[0];
    return {
      isOpen,
      steps: ONBOARDING_STEPS,
      currentStepIndex,
      currentStep: step,
      goToNextStep,
      goToPreviousStep,
      skipOnboarding,
      closeOnboarding,
      startOnboarding,
      restartOnboarding,
      registerElement,
      getElementForId,
      hasCompleted,
      onFetchFrom1177,
      hasCompletedPersonalized,
      startFeatureTour,
      startPersonalizedOnboarding
    };
  }, [
    currentStepIndex,
    getElementForId,
    goToNextStep,
    goToPreviousStep,
    hasCompleted,
    hasCompletedPersonalized,
    isOpen,
    restartOnboarding,
    registerElement,
    skipOnboarding,
    closeOnboarding,
    startOnboarding,
    onFetchFrom1177,
    startFeatureTour,
    startPersonalizedOnboarding
  ]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      {isOpen && (
        <div data-onboarding-active="true" style={{ display: 'contents' }} />
      )}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextValue => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const useOnboardingTarget = (id: string) => {
  const { registerElement } = useOnboarding();
  return useCallback(
    (element: HTMLElement | null) => {
      registerElement(id, element);
    },
    [id, registerElement]
  );
};
